import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { usePageTracking } from '@/hooks/usePageTracking';
import { Register } from '@/pages/Register';
import { Login } from '@/pages/Login';
import { ForgotPassword } from '@/pages/ForgotPassword';
import { ResetPassword } from '@/pages/ResetPassword';
import TemplateDesignerTest from '@/pages/TemplateDesignerTest';
import { Onboarding } from '@/pages/Onboarding';
import { Dashboard } from '@/pages/Dashboard';
import { Clients } from '@/pages/Clients';
import { ViewClient } from '@/pages/ViewClient';
import { Taxes } from '@/pages/Taxes';
import { AddTax } from '@/pages/AddTax';
import { EditTax } from '@/pages/EditTax';
import { Items } from '@/pages/Items';
import { ItemBundles } from '@/pages/ItemBundles';
import { Invoices } from '@/pages/Invoices';
import { CreateInvoice } from '@/pages/CreateInvoice';
import { ViewInvoice } from '@/pages/ViewInvoice';
import { CollectPayment } from '@/pages/CollectPayment';
import { Quotes } from '@/pages/Quotes';
import { CreateQuote } from '@/pages/CreateQuote';
import { ViewQuote } from '@/pages/ViewQuote';
import { CreditMemos } from '@/pages/CreditMemos';
import { CreateCreditMemo } from '@/pages/CreateCreditMemo';
import { ViewCreditMemo } from '@/pages/ViewCreditMemo';
import { ApplyCreditMemo } from '@/pages/ApplyCreditMemo';
import { PurchaseOrders } from '@/pages/PurchaseOrders';
import { CreatePurchaseOrder } from '@/pages/CreatePurchaseOrder';
import { ViewPurchaseOrder } from '@/pages/ViewPurchaseOrder';
import { Payments } from '@/pages/Payments';
import { ViewPayment } from '@/pages/ViewPayment';
import Reports from '@/pages/reports/index';
import OutstandingInvoices from '@/pages/reports/OutstandingInvoices';
import PaymentsReceived from '@/pages/reports/PaymentsReceived';
import RevenueSummary from '@/pages/reports/RevenueSummary';
import InvoiceHistory from '@/pages/reports/InvoiceHistory';
import SalesByClient from '@/pages/reports/SalesByClient';
import SalesTaxSummary from '@/pages/reports/SalesTaxSummary';
import { Settings } from '@/pages/Settings';
import { AddBusiness } from '@/pages/AddBusiness';
import { BusinessSettings } from '@/pages/BusinessSettings';
import { ProfileSettings } from '@/pages/ProfileSettings';
import { TeamSettings } from '@/pages/TeamSettings';
import { Join } from '@/pages/Join';
import { PaymentProcessing } from '@/pages/PaymentProcessing';
import { AccountingSync } from '@/pages/AccountingSync';
import { BillingSettings } from '@/pages/BillingSettings';
import { ClientReviewSettings } from '@/pages/ClientReviewSettings';
import { ReceiptSettings } from '@/pages/ReceiptSettings';
import { PaymentInstructionsSettings } from '@/pages/PaymentInstructionsSettings';
import { AutomatedRemindersSettings } from '@/pages/AutomatedRemindersSettings';
import { NotificationSettings } from '@/pages/NotificationSettings';
import { Reviews } from '@/pages/Reviews';
import { ViewReview } from '@/pages/ViewReview';
import { Notifications } from '@/pages/Notifications';
import { ContractEditor } from '@/pages/ContractEditor';
import { MessageTemplates } from '@/pages/MessageTemplates';
import { QuickBooksCallback } from '@/pages/QuickBooksCallback';
import { StripeReturn } from '@/pages/StripeReturn';
import { StripeRefresh } from '@/pages/StripeRefresh';
import { EmailVerified } from '@/pages/EmailVerified';
import { AppLayout } from '@/components/AppLayout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { RootRedirect } from '@/components/RootRedirect';
import { UpgradeModalProvider } from '@/contexts/UpgradeModalContext';
import { SendBlockModalProvider } from '@/contexts/SendBlockModalContext';
import {
  PublicInvoice,
  PublicQuote,
  PublicPurchaseOrder,
  PublicCreditMemo,
  LegacyHashRedirect,
} from '@/pages/public';

function AppRoutes() {
  usePageTracking();

  return (
    <Routes>
        {/* Public routes */}
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/join/:membershipId" element={<Join />} />

        {/* Root redirect - checks auth and redirects to first business or login */}
        <Route path="/" element={<RootRedirect />} />

        {/* Onboarding - protected but no sidebar */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          }
        />

        {/* Email verified landing page (from one-click verification link) */}
        <Route path="/email-verified" element={<EmailVerified />} />

        {/* QuickBooks OAuth callback - top-level because Intuit redirects without businessId prefix */}
        <Route path="/settings/accounting/quickbooks/callback" element={<ProtectedRoute><QuickBooksCallback /></ProtectedRoute>} />

        {/* Legacy hash redirect - catches old lnk.invoiceowl.com URLs */}
        <Route path="/:legacyHash" element={<LegacyHashRedirect />} />

        {/* Protected routes with shared layout - scoped by business ID */}
        <Route path="/:businessId/*" element={<AppLayout />}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="clients" element={<Clients />} />
          <Route path="clients/:id" element={<ViewClient />} />
          <Route path="taxes" element={<Taxes />} />
          <Route path="taxes/add" element={<AddTax />} />
          <Route path="taxes/:id" element={<EditTax />} />
          <Route path="items" element={<Items />} />
          <Route path="item-bundles" element={<ItemBundles />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="invoices/create" element={<CreateInvoice />} />
          <Route path="invoices/:id" element={<ViewInvoice />} />
          <Route path="invoices/:id/edit" element={<CreateInvoice />} />
          <Route path="payments" element={<Payments />} />
          <Route path="payments/new" element={<CollectPayment />} />
          <Route path="payments/:id" element={<ViewPayment />} />
          <Route path="reports" element={<Reports />} />
          <Route path="reports/outstanding-invoices" element={<OutstandingInvoices />} />
          <Route path="reports/payments" element={<PaymentsReceived />} />
          <Route path="reports/revenue" element={<RevenueSummary />} />
          <Route path="reports/invoices" element={<InvoiceHistory />} />
          <Route path="reports/sales-by-client" element={<SalesByClient />} />
          <Route path="reports/sales-tax" element={<SalesTaxSummary />} />
          <Route path="reviews" element={<Reviews />} />
          <Route path="reviews/:id" element={<ViewReview />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="quotes" element={<Quotes />} />
          <Route path="quotes/create" element={<CreateQuote />} />
          <Route path="quotes/:id" element={<ViewQuote />} />
          <Route path="quotes/:id/edit" element={<CreateQuote />} />
          <Route path="credit-memos" element={<CreditMemos />} />
          <Route path="credit-memos/create" element={<CreateCreditMemo />} />
          <Route path="credit-memos/:id" element={<ViewCreditMemo />} />
          <Route path="credit-memos/:id/edit" element={<CreateCreditMemo />} />
          <Route path="credit-memos/:id/apply" element={<ApplyCreditMemo />} />
          <Route path="purchase-orders" element={<PurchaseOrders />} />
          <Route path="purchase-orders/create" element={<CreatePurchaseOrder />} />
          <Route path="purchase-orders/:id" element={<ViewPurchaseOrder />} />
          <Route path="purchase-orders/:id/edit" element={<CreatePurchaseOrder />} />
          <Route path="settings" element={<Settings />} />
          <Route path="settings/profile" element={<ProfileSettings />} />
          <Route path="settings/business" element={<BusinessSettings />} />
          <Route path="settings/team" element={<TeamSettings />} />
          <Route path="settings/payments" element={<PaymentProcessing />} />
          <Route path="settings/accounting" element={<AccountingSync />} />
          <Route path="settings/accounting/quickbooks/callback" element={<QuickBooksCallback />} />
          <Route path="settings/billing" element={<BillingSettings />} />
          <Route path="settings/client-reviews" element={<ClientReviewSettings />} />
          <Route path="settings/receipts" element={<ReceiptSettings />} />
          <Route path="settings/payment-instructions" element={<PaymentInstructionsSettings />} />
          <Route path="settings/reminders" element={<AutomatedRemindersSettings />} />
          <Route path="settings/notifications" element={<NotificationSettings />} />
          <Route path="settings/message-templates" element={<MessageTemplates />} />
          <Route path="stripe/return" element={<StripeReturn />} />
          <Route path="stripe/refresh" element={<StripeRefresh />} />
          <Route path="template-designer" element={<TemplateDesignerTest />} />
          <Route path="contract-editor" element={<ContractEditor />} />
          <Route path="add-business" element={<AddBusiness />} />
        </Route>

        {/* Public Document Routes (no authentication required) */}
        <Route path="/invoice/:id" element={<PublicInvoice />} />
        <Route path="/quote/:id" element={<PublicQuote />} />
        <Route path="/purchase-order/:id" element={<PublicPurchaseOrder />} />
        <Route path="/credit-memo/:id" element={<PublicCreditMemo />} />
      </Routes>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || ''}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <UpgradeModalProvider>
            <SendBlockModalProvider>
              <Toaster position="bottom-right" richColors />
              <AppRoutes />
            </SendBlockModalProvider>
          </UpgradeModalProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
